import type { UserGender } from "@teamz/common";
import type { UrlKeys } from "nuqs";

import { useQuery } from "@tanstack/react-query";
import { playerGenders } from "@teamz/common";
import { Trophy } from "lucide-react";
import { parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { FilterBarTournamentList } from "@/components/filter-bar/filter-bar-tournament-list";
import { PremiumBanner, PremiumFeatureCard } from "@/components/premium-upsell";
import { ListSkeleton } from "@/components/skeleton/list-skeleton";
import { UpsertTournament } from "@/components/tournaments/upsert-tournament";
import { useMe } from "@/context/user-context";
import { useBreakpoints } from "@/hooks/use-breakpoints";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { useTRPC } from "@/lib/trpc";
import { routes } from "@/routes";

import { PlansPage } from "../plans";
import { TournamentHeader } from "./tournament-header";
import { TournamentListItem } from "./tournament-list-item";

type TournamentType = RouterOutputs["tournament"]["getTournamentList"][number];

const searchParams = {
  search: parseAsString.withDefault(""),
  genderFilter: parseAsStringLiteral(playerGenders),
  surfaceFilter: parseAsStringLiteral(["clay", "hard"]),
};
const searchParamsKeys: UrlKeys<typeof searchParams> = {
  search: "search",
  genderFilter: "g",
  surfaceFilter: "s",
};

export function TournamentList() {
  const { user } = useMe();
  const trpc = useTRPC();
  const { data, status } = useQuery(trpc.tournament.getTournamentList.queryOptions({ userId: user.id }));
  const { data: playersStats } = useQuery(trpc.player.getPlayersStats.queryOptions(undefined, { enabled: user.role === "club" }));
  const { t } = useTranslation(["tournaments", "common", "profile"]);
  const [{ search, genderFilter, surfaceFilter }, setSearchFilters] = useQueryStates(searchParams, { urlKeys: searchParamsKeys });
  const { getCountryName } = useTranslationUtils();
  const breakpoint = useBreakpoints();

  if (user.plan === "pack_equipe") {
    return <PlansPage />;
  }

  // Club freemium : la page reste entièrement utilisable, y compris la création.
  // Les tournois sont enregistrés en brouillon et un bandeau explique ce qui
  // manque pour les rendre visibles des joueurs.
  const isFreemiumClub = user.role === "club" && !user.plan && !user.isAdmin;

  if (status === "pending") {
    return <ListSkeleton />;
  }
  if (status === "error") {
    return null;
  }

  // Club freemium sans tournoi : trois bandeaux empilés (titre de page, argumentaire
  // d'abonnement, liste vide) pour un écran qui n'a rien à montrer. On le remplace par
  // une carte unique dont l'action principale reste la création, ouverte sans abonnement.
  if (isFreemiumClub && data.length === 0) {
    return (
      <PremiumFeatureCard
        icon={<Trophy className="size-6" />}
        title={t("common:freemium.tournaments-title")}
        description={t("common:freemium.tournaments-description")}
        benefits={[
          t("common:freemium.tournaments-benefit-1"),
          // Le nombre de joueurs est l'argument le plus concret, mais il n'a de sens
          // que si la stat a bien été chargée.
          ...(playersStats?.allPlayersStat
            ? [t("common:freemium.tournaments-benefit-2", { count: playersStats.allPlayersStat })]
            : []),
          t("common:freemium.tournaments-benefit-3"),
        ]}
        primaryAction={<UpsertTournament className="w-full" />}
        secondaryLabel={t("common:freemium.see-plans")}
        secondaryTo={routes.plans}
      />
    );
  }

  const filterBySearch = (tournament: TournamentType, search: string) => {
    const searchTerm = search.toLowerCase();
    return (
      tournament.name?.toLowerCase().includes(searchTerm)
      || tournament.clubName?.toLowerCase().includes(searchTerm)
      || tournament.city?.toLowerCase().includes(searchTerm)
      || getCountryName(tournament.country).toLowerCase().includes(searchTerm)
    );
  };

  const filterByGender = (player: TournamentType, genderFilter: UserGender | null) => {
    if (!genderFilter)
      return true;
    return player.tournamentPlayers.some(player => player.gender === genderFilter);
  };

  const filterBySurface = (player: TournamentType, surfaceFilter: "clay" | "hard" | null) => {
    if (!surfaceFilter)
      return true;
    return player.tournamentSurface?.includes(surfaceFilter);
  };

  const filteredData = data.filter(tournament =>
    filterBySearch(tournament, search)
    && filterByGender(tournament, genderFilter)
    && filterBySurface(tournament, surfaceFilter),
  );

  return (
    <div className="container flex flex-col gap-4 md:p-4">
      {user.role === "club" && <TournamentHeader />}

      {isFreemiumClub && (
        <PremiumBanner
          description={t("common:freemium.tournaments-banner-description", { count: playersStats?.allPlayersStat ?? 0 })}
        />
      )}

      {user.role === "player" && (
        <FilterBarTournamentList
          search={search}
          gender={genderFilter ?? ""}
          surface={surfaceFilter ?? ""}
          setSearch={value => setSearchFilters({ search: value })}
          setGenderFilter={value => setSearchFilters({ genderFilter: value })}
          setSurfaceFilter={value => setSearchFilters({ surfaceFilter: value })}
        />
      )}

      <div className="flex flex-col gap-4 px-2 pb-4 md:gap-0 md:rounded-[18px] md:border md:border-neutral-400 md:p-0 lg:border">
        {
          (breakpoint === "lg" || breakpoint === "xl") && filteredData.length > 0 && (
            <div
              className="hidden grid-cols-[300px,55px,80px,140px,auto,120px] items-center gap-2.5 rounded-t-[18px] bg-neutral-50 px-2.5 py-2 text-xs font-medium text-neutral-500 lg:grid"
            >
              <span className="py-2 pl-2">
                {`${t("common:desktop-nav.tournaments")}  (${filteredData.length})`}
              </span>

              <span className="py-2 text-center">
                {t("common:country")}
              </span>

              <span className="text-nowrap py-2 text-center">
                {t("tournaments:winner-prize")}
              </span>

              <span className="py-2 text-center">
                {t("profile:surface")}
              </span>

              <div className="flex items-center gap-4">
                <div className="flex w-full justify-center text-nowrap ">
                  <span>{t("tournaments:tournament-start-date")}</span>
                </div>

                <div className="flex w-full justify-center text-nowrap ">
                  <span>{t("tournaments:tournament-end-date")}</span>
                </div>
              </div>

              <span className="py-2 text-center">
                Actions
              </span>
            </div>
          )
        }

        {filteredData.length > 0
          ? filteredData.map(tournament => (
              <TournamentListItem tournament={tournament} key={tournament.id} fromPath={routes.tournaments} />
            ))
          : (
              <span
                className="rounded-[18px] border border-neutral-400 bg-white p-4 text-center text-neutral-600 md:border-0"
              >
                {t("tournaments:empty-tournament-list")}
              </span>
            )}
      </div>
    </div>
  );
}
